/**
 * DUPLICATED FILE
 * This is a generic version of study-insights/utils/sampleViewGraph.ts
 * TODO: We should replace sampleViewGraph.ts with this generic version.
 */

import {
  CHART_KEY_DAILY_DATE_FORMAT,
  formatDate,
  getFirstDayOfWeek,
  getLastDayOfWeek,
  getSelectedDateRangeString,
  isDateBetween,
} from '@common/utils';
import { add, differenceInDays, getMonth, getWeekOfMonth, getYear, isAfter, isBefore, isSameYear } from 'date-fns';
import { Serie, DynamicDateOptions } from '../../shared/types/DynamicBarChart.types';

const DYNAMIC_MAX_DAILY_DAYS = 15;
const DYNAMIC_MAX_WEEKLY_DAYS = 90;

export const DATE_FORMAT_DAILY = 'EEE d';
export const DATE_FORMAT_WEEKLY = 'MMM d';
export const DATE_FORMAT_MONTHLY = 'MMM yyyy';

export const defaultDynamicOptions: DynamicDateOptions = {
  dailyDateFormat: DATE_FORMAT_DAILY,
  weeklyDateFormat: DATE_FORMAT_WEEKLY,
  monthlyDateFormat: DATE_FORMAT_MONTHLY,
};

const buildBarData = (groupValue: string, value: number = 0, converted: number = 0): Serie => {
  return {
    converted,
    groupValue,
    value,
  };
};

/**
 * Returns a string to represent a daily data given a serie group value and a date format.
 * @param Serie object that can represent a sequence sample or a participant recruirment serie.
 * @param dateFormat
 * @returns date format string
 */
const generateDailyGroupName = ({ groupValue }: Serie, dateFormat: string): string => {
  const groupValueWithoutTZ = groupValue.replace('Z', '');
  return formatDate(groupValueWithoutTZ, dateFormat);
};

/**
 * Returns a string to represent a weekly data given a serie group value and a date format.
 * @param Serie object that can represent a sequence sample or a participant recruirment serie.
 * @returns string representing weekly view graph.
 */
const generateWeeklyGroupName = (
  { groupValue }: Serie,
  _dateFormat: string,
  startDate?: Date,
  endDate?: Date,
): string => {
  const groupValueWithoutTZ = groupValue.replace('Z', '');
  const date: Date = new Date(groupValueWithoutTZ);

  if (startDate !== undefined && endDate !== undefined && isDateBetween(date, startDate, endDate)) {
    let firstDayOfWeek: Date = getFirstDayOfWeek(date);
    let lastDayOfWeek: Date = getLastDayOfWeek(date);
    if (isBefore(firstDayOfWeek, startDate)) firstDayOfWeek = startDate;
    if (isAfter(lastDayOfWeek, endDate)) lastDayOfWeek = endDate;
    return getSelectedDateRangeString(firstDayOfWeek, lastDayOfWeek, true);
  }
  return '';
};

/**
 * Returns a string to represent a monthly data given a serie group value and a date format.
 * @param Serie object that can represent a sequence sample or a participant recruirment serie.
 * @param dateFormat
 * @returns date format string
 */
const generateMonthlyGroupName = ({ groupValue }: Serie, dateFormat: string): string => {
  const groupValueWithoutTZ = groupValue.replace('Z', '');
  return formatDate(groupValueWithoutTZ, dateFormat);
};

/**
 * Generates graph data given serie array.
 * @param data Serie array.
 * @param graphData Initial data to repesent the graph.
 * @param dateFormat date format string
 * @param buildGroupName Function to build the name of the grpah data
 * @param propNames Serie props names to build the data from. Each specified prop value will be reduced.
 * @returns Graph data record object
 */
const generateGraphData = (
  data: Serie[],
  graphData: Record<string, Serie>,
  dateFormat: string,
  buildGroupName: (data: Serie, dateFormat: string, startDate?: Date, endDate?: Date) => string,
  propNames: Array<keyof Serie> = ['value'],
  startDate?: Date,
  endDate?: Date,
): Record<string, Serie> => {
  data.forEach((item) => {
    const groupName = buildGroupName(item, dateFormat, startDate, endDate);
    if (graphData[groupName] !== undefined && graphData[groupName]) {
      propNames.forEach((propName) => {
        if (graphData[groupName][propName] !== undefined) {
          // @ts-expect-error
          graphData[groupName][propName] += item[propName];
        }
      });
    }
  });
  return graphData;
};

/**
 * Generates daily graph data given serie array.
 * @param data Serie array.
 * @param startDate date object to start building graph data from
 * @param endDate date object to end building graph data
 * @param dateFormat date format string
 * @param propNames Serie props names to build the data from. Each specified prop value will be reduced. This prop is passed directly to generateGraphData() function.
 * @returns Graph data record object
 */
export const buildDailyData = (
  data: Serie[],
  startDate: Date,
  endDate: Date,
  dateFormat: string,
  propNames?: Array<keyof Serie>,
  showMonth: Boolean = false,
): Record<string, Serie> => {
  let graphData: Record<string, Serie> = {};
  for (let d = new Date(startDate); d <= new Date(endDate); d.setDate(d.getDate() + 1)) {
    const groupName = formatDate(d, dateFormat);
    const key = formatDate(d, CHART_KEY_DAILY_DATE_FORMAT);
    if (showMonth) {
      const month = formatDate(d, 'MMM');
      const groupNameWithMonth = `${groupName}\n ${month}`;
      graphData[key] = buildBarData(groupNameWithMonth);
    } else {
      graphData[key] = buildBarData(groupName);
    }
  }

  graphData = generateGraphData(data, graphData, CHART_KEY_DAILY_DATE_FORMAT, generateDailyGroupName, propNames);
  return graphData;
};

/**
 * Generates weekly graph data given serie array.
 * @param data Serie array.
 * @param startDate date object to start building graph data from
 * @param endDate date object to end building graph data
 * @param dateFormat date format string
 * @param propNames Serie props names to build the data from. Each specified prop value will be reduced. This prop is passed directly to generateGraphData() function.
 * @returns Graph data record object
 */
export const buildWeeklyData = (
  data: Serie[],
  startDate: Date,
  endDate: Date,
  dateFormat: string,
  propNames?: Array<keyof Serie>,
): Record<string, Serie> => {
  const firstWeek: number = getWeekOfMonth(startDate);
  const secondWeek: number = firstWeek + 1;
  const lastWeek: number = getWeekOfMonth(endDate);
  let graphData: Record<string, Serie> = {};
  let firstDayOfWeek: Date = startDate;
  let lastDayOfWeek: Date = getLastDayOfWeek(startDate);

  for (let w = firstWeek; w <= lastWeek; w++) {
    if (w === secondWeek) {
      firstDayOfWeek = add(getFirstDayOfWeek(startDate), {
        weeks: 1,
      });
      lastDayOfWeek = add(lastDayOfWeek, {
        weeks: 1,
      });
    } else if (w !== firstWeek && w !== lastWeek) {
      firstDayOfWeek = add(firstDayOfWeek, {
        weeks: 1,
      });
      lastDayOfWeek = add(lastDayOfWeek, {
        weeks: 1,
      });
    } else if (w === lastWeek) {
      firstDayOfWeek = add(firstDayOfWeek, {
        weeks: 1,
      });
      lastDayOfWeek = endDate;
    }

    const groupName = getSelectedDateRangeString(firstDayOfWeek, lastDayOfWeek, true);
    const groupNameWithWeek = `${groupName}\nWeek ${w}`;

    graphData[groupName.toString()] = buildBarData(groupNameWithWeek);
  }

  graphData = generateGraphData(data, graphData, dateFormat, generateWeeklyGroupName, propNames, startDate, endDate);
  return graphData;
};

/**
 * Generates monthly graph data given serie array.
 * @param data Serie array.
 * @param startDate date object to start building graph data from
 * @param endDate date object to end building graph data
 * @param dateFormat date format string
 * @param propNames Serie props names to build the data from. Each specified prop value will be reduced. This prop is passed directly to generateGraphData() function.
 * @returns Graph data record object
 */
export const buildMonthlyData = (
  data: Serie[],
  startDate: Date,
  endDate: Date,
  dateFormat: string,
  propNames?: Array<keyof Serie>,
): Record<string, Serie> => {
  let graphData: Record<string, Serie> = {};

  const startDateMonth = getMonth(startDate);
  const endDateMonth = getMonth(endDate);

  if (isSameYear(startDate, endDate)) {
    for (let i = startDateMonth; i <= endDateMonth; i++) {
      const groupName = formatDate(new Date(startDate.getFullYear(), i, 1), dateFormat);
      graphData[groupName.toString()] = buildBarData(groupName);
    }
  } else {
    const startDateYear = getYear(startDate);
    const endDateYear = getYear(endDate);

    for (let y = startDateYear; y <= endDateYear; y++) {
      if (y === startDateYear) {
        for (let m = startDateMonth; m <= 11; m++) {
          const groupName = formatDate(new Date(y, m, 1), dateFormat);
          graphData[groupName.toString()] = buildBarData(groupName);
        }
      } else if (y === endDateYear) {
        for (let m = 0; m <= endDateMonth; m++) {
          const groupName = formatDate(new Date(y, m, 1), dateFormat);
          graphData[groupName.toString()] = buildBarData(groupName);
        }
      } else {
        for (let i = 0; i <= 11; i++) {
          const groupName = formatDate(new Date(y, i, 1), dateFormat);
          graphData[groupName.toString()] = buildBarData(groupName);
        }
      }
    }
  }

  graphData = generateGraphData(data, graphData, dateFormat, generateMonthlyGroupName, propNames);

  return graphData;
};

/**
 * Generates dynamic graph data based on given date range and serie array.
 * @param data Serie array.
 * @param startDate date object to start building graph data from
 * @param endDate date object to end building graph data
 * @param propNames Serie props names to build the data from. Each specified prop value will be reduced. This prop is passed directly to generateGraphData() function.
 * @returns Graph data object with data and according date format
 */
export const buildDynamicDateRange = (
  data: Serie[],
  startDate: Date,
  endDate: Date,
  options: Partial<DynamicDateOptions> = defaultDynamicOptions,
  propNames?: Array<keyof Serie>,
): {
  dateFormat: string;
  data: Record<string, Serie>;
} => {
  let graphData: Record<string, Serie> = {};
  let dateFormat;

  const diffInDays = differenceInDays(endDate, startDate);
  if (diffInDays <= DYNAMIC_MAX_DAILY_DAYS) {
    dateFormat = options.dailyDateFormat ?? defaultDynamicOptions.dailyDateFormat;
    graphData = { ...buildDailyData(data, startDate, endDate, dateFormat, propNames) };
  } else if (diffInDays > DYNAMIC_MAX_DAILY_DAYS && diffInDays <= DYNAMIC_MAX_WEEKLY_DAYS) {
    dateFormat = options.weeklyDateFormat ?? defaultDynamicOptions.weeklyDateFormat;
    graphData = { ...buildWeeklyData(data, startDate, endDate, dateFormat, propNames) };
  } else {
    dateFormat = options.monthlyDateFormat ?? defaultDynamicOptions.monthlyDateFormat;
    graphData = { ...buildMonthlyData(data, startDate, endDate, dateFormat, propNames) };
  }

  return {
    dateFormat,
    data: graphData,
  };
};
